/**
 * HypeFly · Aktions-Landing (`/aktion`)
 * =====================================
 *
 * Direct-Response-Landingpage für TikTok- und Meta-Ads-Traffic. Bewusst
 * laut, bold und Conversion-fokussiert — bricht mit der Editorial-
 * Ästhetik der Startseite und richtet sich klar an den Massenmarkt.
 *
 * WICHTIG:
 *  - Diese Seite verändert die Editorial-Homepage (`Home.tsx`) nicht.
 *  - Sie wird von `/aktion` immer geladen (unabhängig vom
 *    `homepage_mode`) und von `/` nur dann, wenn `homepage_mode.mode`
 *    im CMS auf `"aktion"` gesetzt ist.
 *  - Analytics greift wie überall (siehe `useAnalytics()` in App.js /
 *    root layout). `/aktion` ist in `_KNOWN_PUBLIC_ROUTES_EXACT`
 *    aufgenommen, damit `page_view`-Events nicht als „Scanner" gedroppt
 *    werden.
 *
 * Sektionen (Reihenfolge = im User-Auftrag verabschiedet):
 *  A) Header (sticky, kompakt)
 *  B) Hero — Sky + Headline + Brushstroke + Siegel + Gold-CTA + Slots
 *  C) 4 Feature-Cards
 *  D) „Warum kostenlos?" — dunkler Trust-Block
 *  E) Live-360°-Demo (echter Video360Viewer)
 *  F) 4-Schritte-Prozess
 *  G) Urgency-Countdown + zweiter CTA
 *  H) Kurz-Kontaktformular
 *  I) Footer (identisch zur Editorial-Startseite)
 */
import { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import {
    Camera, Compass, Film, Home as HomeIcon, Cloud, Monitor,
    Shield, Lock, CheckCircle2, ChevronRight, ChevronDown,
} from "lucide-react";
import BrandLogo from "@/components/HeaderBrandLogo";
import SiteFooter from "@/components/HFFooter";
import { useSiteContent } from "@/lib/siteContent";
import Video360Viewer from "@/components/Video360Viewer";
import ViewerErrorBoundary from "@/components/ViewerErrorBoundary";
void Video360Viewer; void ViewerErrorBoundary; // legacy imports — Vergleichs-Sektion nutzt jetzt AktionComparisonSlot
import AktionComparisonSlot from "@/components/AktionComparisonSlot";
import AktionSky from "@/components/AktionSky";
import AktionSeal from "@/components/AktionSeal";
import AktionBrush from "@/components/AktionBrush";
import AktionContactForm from "@/components/AktionContactForm";
import GutscheinPopup, { type GutscheinMode } from "@/components/GutscheinPopup";
import WelcomeChoicePopup from "@/components/WelcomeChoicePopup";
import HeroChoiceTiles from "@/components/HeroChoiceTiles";
import HeroVideoTiles from "@/components/HeroVideoTile";
import AktionVideoHero from "@/components/AktionVideoHero";
import { useAktionsSeite, type SpecialOfferStyle } from "@/lib/useAktionsSeite";
import "@/styles/aktion.css";

/* Typografie-Mapping für die Special-Offer-Sektion. Dynamische Styles
 * werden als inline CSS gesetzt, damit User-Werte (Hex-Farbe, Custom-Size,
 * Font-Familie) 1:1 durchkommen ohne Tailwind-Purge-Probleme. */
const SPECIAL_OFFER_SIZE_PX: Record<SpecialOfferStyle["size"], string> = {
    sm: "1rem",       // 16px
    md: "1.25rem",    // 20px
    lg: "1.5rem",     // 24px
    xl: "2.25rem",    // 36px
    "2xl": "3rem",    // 48px
    "3xl": "4.5rem",  // 72px
};
const SPECIAL_OFFER_FONT_FAMILY: Record<SpecialOfferStyle["font_family"], string> = {
    serif: "var(--hf-font-display, 'Cormorant Garamond', Georgia, serif)",
    display: "var(--hf-font-display, 'Cormorant Garamond', Georgia, serif)",
    sans: "var(--hf-font-body, Inter, system-ui, sans-serif)",
    anton: "'Anton', 'Impact', 'Arial Narrow', sans-serif",
};
const SPECIAL_OFFER_WEIGHT: Record<SpecialOfferStyle["weight"], number> = {
    normal: 400, bold: 700, black: 900,
};
function specialOfferStyleToCss(s: SpecialOfferStyle): React.CSSProperties {
    return {
        fontSize: SPECIAL_OFFER_SIZE_PX[s.size] || SPECIAL_OFFER_SIZE_PX.md,
        color: s.color || "#0F1830",
        fontFamily: SPECIAL_OFFER_FONT_FAMILY[s.font_family] || SPECIAL_OFFER_FONT_FAMILY.sans,
        fontWeight: SPECIAL_OFFER_WEIGHT[s.weight] || 400,
        textTransform: s.uppercase ? "uppercase" : "none",
        lineHeight: 1.2,
    };
}

/** Mapping: icon-name (aus CMS) → lucide-Component. */
const ICONS: Record<string, React.ComponentType<{ size?: number; strokeWidth?: number; className?: string }>> = {
    camera: Camera,
    compass: Compass,
    film: Film,
    home: HomeIcon,
    cloud: Cloud,
    monitor: Monitor,
};

function IconFromName({ name, size = 32 }: { name: string; size?: number }) {
    const Cmp = ICONS[name] || Camera;
    return <Cmp size={size} strokeWidth={1.8} />;
}

/** Slots-Anzeige mit pulsierendem Gold-Ring — wiederverwendbar.
 *  `size="sm"` reduziert Padding + Font-Size um ~25 % (für kompakte
 *  Layout-Kontexte wie das Hero-Content-Band neben dem Welcome-Side-
 *  Panel). Der rote Dot bleibt bewusst gleich groß, damit die Wirkung
 *  „Warnung / Verknappung" erhalten bleibt. */
function SlotsChip({
    remaining, total, label = "noch frei", size = "default",
}: {
    remaining: number; total: number; label?: string; size?: "default" | "sm";
}) {
    const soldOut = remaining <= 0;
    const sizeCls = size === "sm"
        ? "px-4 py-1.5 text-sm md:text-base"
        : "px-5 py-2.5 text-xl";
    return (
        <div
            className={`inline-flex items-center gap-3 rounded-full font-anton uppercase ${sizeCls}${
                soldOut ? "" : " hf-slots-pulse hf-slots-pulse-medium"
            }`}
            style={{
                background: soldOut ? "#F1F5F9" : "linear-gradient(180deg, #F1D48A 0%, #D4A855 100%)",
                color: "#0F1830",
                border: soldOut ? "2px solid #CBD5E1" : "2px solid #8A6A2C",
                boxShadow: soldOut ? "none" : "0 6px 18px -6px rgba(228, 180, 67, 0.7)",
                letterSpacing: "0.05em",
            }}
            data-testid="aktion-slots-chip"
        >
            <span aria-hidden style={{
                width: 12, height: 12, borderRadius: "50%",
                background: soldOut ? "#94A3B8" : "#DC2626",
                boxShadow: soldOut ? "none" : "0 0 8px rgba(220, 38, 38, 0.7)",
            }} />
            {soldOut
                ? "Aktion vollständig belegt"
                : <><strong style={{ fontWeight: 700 }}>{remaining}</strong> von {total} {label}</>}
        </div>
    );
}

export default function AktionHome() {
    const cfg = useAktionsSeite();
    const { getObject } = useSiteContent();
    // Selbes Branding-Logo wie auf der Editorial-Startseite → gepflegt unter
    // /admin/startseite → Panel „Branding". Wenn `media_id` gesetzt und das
    // Media ein Video ist, rendert `HeaderBrandLogo` automatisch das
    // animierte MP4/WebM mit autoplay + muted + loop + playsInline.
    const brandLogo = getObject<{ media_id?: string | null }>("brand_logo", { media_id: null });
    const remaining = Math.max(0, cfg.counter_total - cfg.counter_slots_taken);
    const [openFaq, setOpenFaq] = useState<number | null>(null);

    // Beim Mount nach oben scrollen (kann per Ad-Link-Anker verschoben werden).
    useEffect(() => {
        if (typeof window !== "undefined" && !window.location.hash) {
            window.scrollTo({ top: 0, behavior: "auto" });
        }
    }, []);

    // Welcome-Choice-Flow: nutzt einen Nonce + Mode, um das existierende
    // GutscheinPopup extern zu öffnen (unabhängig von Scroll/Zeit-Trigger).
    // `openSignal=0` = kein Auto-Open; jede Erhöhung öffnet mit `manualMode`.
    const [manualMode, setManualMode] = useState<GutscheinMode>("rabatt");
    const [openSignal, setOpenSignal] = useState<number>(0);
    // `welcomeDismissed` merkt sich (session-persistent), ob der Nutzer
    // das Welcome-Popup abgelehnt hat (X / „Zuerst informieren" / Escape
    // / Backdrop-Klick / Karten-Klick). Sobald `true`:
    //   1. Das Welcome-Popup wird nicht mehr gerendert (auch nach Reload
    //      in derselben Tab-Session).
    //   2. Der Rabatt-Popup-Auto-Trigger bleibt permanent aus (kein
    //      Second-Chance-Nachtrigger, siehe `suspendAutoTrigger` unten) —
    //      der Nutzer behält die Kontrolle. Manuelle Karten-Klicks über
    //      `openSignal` bleiben davon unberührt.
    // Persistiert in `sessionStorage.hf-welcome-dismissed = "1"` — bei
    // neuem Tab / Browser ist das Feld wieder leer und alles startet
    // frisch.
    const WELCOME_DISMISSED_KEY = "hf-welcome-dismissed";
    const [welcomeDismissed, setWelcomeDismissed] = useState<boolean>(() => {
        if (typeof window === "undefined") return false;
        try {
            return window.sessionStorage.getItem(WELCOME_DISMISSED_KEY) === "1";
        } catch {
            return false;
        }
    });
    const handleWelcomeChoice = (choice: GutscheinMode) => {
        setManualMode(choice);
        setOpenSignal((n) => n + 1);
        // Karten-Klick zählt auch als Dismiss — das Welcome-Popup soll
        // nicht erneut erscheinen bei Reload, und der Auto-Trigger bleibt
        // aus (weil er sowieso permanent aus ist, s. `suspendAutoTrigger`).
        setWelcomeDismissed(true);
        try { window.sessionStorage.setItem(WELCOME_DISMISSED_KEY, "1"); } catch { /* Storage kann geblockt sein */ }
    };
    const handleWelcomeDismissed = () => {
        setWelcomeDismissed(true);
        try { window.sessionStorage.setItem(WELCOME_DISMISSED_KEY, "1"); } catch { /* Storage kann geblockt sein */ }
    };

    // Split-Headline für „X Häuser" rot hervorheben.
    const sublineParts = useMemo(() => {
        // Ersetze die konkrete Zahl im Subline durch den echten Counter-Wert,
        // damit CMS-Update auf `counter_total` sich sofort im Text spiegelt.
        const nStr = String(cfg.counter_total);
        const marker = /(\d+ Häuser)/;
        const src = cfg.hero_subline.replace(/\d+ Häuser/, `${nStr} Häuser`);
        return src.split(marker);
    }, [cfg.hero_subline, cfg.counter_total]);

    const scrollToForm = () => {
        const el = document.getElementById("aktion-kontakt");
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    return (
        <div style={{ background: "#F8FAFC" }}>
            <Helmet>
                <title>HypeFly Aktion — 100 Häuser kostenlos in 360° dokumentiert</title>
                <meta
                    name="description"
                    content="Wir suchen 100 Häuser für unser Referenzprojekt. Komplette 360°-Dokumentation Ihres Zuhauses — für Sie 100% kostenlos. Jetzt unverbindlich anfragen."
                />
                <meta property="og:title" content="HypeFly Aktion — 100 Häuser kostenlos in 360°" />
                <meta property="og:description" content="Wir suchen 100 Häuser für unser Referenzprojekt. Komplette 360°-Dokumentation Ihres Zuhauses — für Sie 100% kostenlos." />
                <meta property="og:type" content="website" />
                <link rel="canonical" href="https://hypefly.de/aktion" />
                {/* Anton-Font-Preload: erlaubt dem Browser, das Font parallel
                 *  zum HTML-Parsen zu laden. Kein Cross-Origin, da lokal. */}
                <link rel="preload" as="font" type="font/ttf" href="/fonts/anton-regular.ttf" crossOrigin="" />
            </Helmet>

            {/* ═══════════════ A) HEADER ═══════════════ */}
            <header
                className="sticky top-0 z-40 backdrop-blur-md"
                style={{ background: "rgba(255,255,255,0.92)", borderBottom: "1px solid #E2E8F0" }}
                data-testid="aktion-header"
            >
                <div className="max-w-6xl mx-auto px-4 md:px-6 min-h-[76px] md:min-h-[120px] xl:min-h-[140px] py-2 md:py-3 flex items-center justify-between gap-3">
                    <Link
                        to="/aktion"
                        className="flex items-center"
                        aria-label="HypeFly · Premium 360°"
                        data-testid="aktion-brand"
                    >
                        <BrandLogo
                            mediaId={brandLogo.media_id}
                            className="shrink-0 h-14 md:h-24 lg:h-28 xl:h-32 w-[164px] md:w-[284px] lg:w-[332px] xl:w-[380px] object-contain"
                            testId="aktion-header-logo-mark"
                        />
                    </Link>
                    <nav className="hidden md:flex items-center gap-6 text-sm font-medium" style={{ color: "#0F1830" }}>
                        <a href="#ablauf" className="hover:opacity-70">Ablauf</a>
                        <a href="#faq" className="hover:opacity-70">FAQ</a>
                        <a href="#aktion-kontakt" className="hover:opacity-70">Kontakt</a>
                        <button
                            type="button"
                            onClick={scrollToForm}
                            className="px-4 py-2 rounded-md font-anton uppercase text-sm tracking-wider"
                            style={{ background: "transparent", color: "#8A6A2C", border: "2px solid #D4A855" }}
                            data-testid="aktion-header-cta"
                        >
                            Projekt anfragen
                        </button>
                    </nav>
                    <button
                        type="button"
                        onClick={scrollToForm}
                        className="md:hidden px-3 py-1.5 rounded-md font-anton uppercase text-xs tracking-wider"
                        style={{ background: "#D4A855", color: "#0F1830" }}
                    >
                        Anfragen
                    </button>
                </div>
            </header>

            {/* ═══════════════ A2) VIDEO-HERO (optional, oben) ═══════════════
             *
             * Full-width Cinema-Video ganz oben, zwischen Header und dem
             * bestehenden AUFGEPASST-Hero. Wenn `video_hero.enabled=false`,
             * bleibt das ursprüngliche Layout unverändert.
             */}
            {cfg.video_hero?.enabled && <AktionVideoHero cfg={cfg.video_hero} />}

            {/* ═══════════════ B) HERO ═══════════════
             *
             * Zwei-Band-Aufbau wie im Original-Flyer:
             *   1) Foto-Band (55–60% der Hero-Höhe) — Haus-Foto als Kulisse
             *      für Headline, roten Brushstroke und Phone+Seal-Overlay.
             *   2) Cream-Band (unteres Panel) — dunkler, klar lesbarer Text:
             *      Subline · CTA · Slots-Chip. Sitzt auf `#FFFDF9` statt auf
             *      dem Foto → satter Kontrast, hochwertiger Look.
             */}
            <section
                className="relative overflow-hidden"
                data-testid="aktion-hero"
            >
                {/* ── Band 1: Foto-Kulisse ── */}
                <div
                    className="relative pt-10 md:pt-14 pb-8 md:pb-12"
                    style={{ zIndex: 1 }}
                    data-testid="aktion-hero-photo-band"
                >
                    <AktionSky />
                    <div className="max-w-6xl mx-auto px-4 md:px-6 relative" style={{ zIndex: 1 }}>
                        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_380px] gap-6 lg:gap-12 items-start">
                            <div>
                                <h1
                                    className="hf-aktion-headline font-anton uppercase leading-[0.88] font-black text-[10.5vw] md:text-[9vw] lg:text-[8.5rem]"
                                    style={{ letterSpacing: "-0.01em" }}
                                    data-testid="aktion-hero-headline"
                                >
                                    {cfg.hero_headline}
                                </h1>
                                <div className="mt-4 md:mt-6 max-w-[560px]" style={{ transform: "rotate(-1.5deg)" }}>
                                    <AktionBrush text={cfg.hero_brushstroke} />
                                </div>
                            </div>
                            {/* Phone-Mockup + überlappender Zackenkreis.
                             *
                             * Mobile: `flex-col-reverse` → Siegel oben, Phone
                             * darunter, kein Überlappen. Desktop (md+): normaler
                             * Block-Container, Siegel absolut positioniert oben
                             * rechts über dem Phone (Flyer-Look). */}
                            <div className="relative flex flex-col-reverse items-end gap-4 md:block md:gap-0 justify-self-end mt-6 lg:mt-0">
                                <img
                                    src="/media/aktion/phone-360-preview.png"
                                    alt="360°-Ansicht auf dem Handy — Live-Vorschau"
                                    className="relative block h-[190px] md:h-[380px] w-auto"
                                    style={{
                                        filter: "drop-shadow(0 24px 40px rgba(0,0,0,0.35))",
                                        zIndex: 1,
                                    }}
                                    data-testid="aktion-phone-mockup"
                                    loading="eager"
                                    decoding="async"
                                />
                                <div
                                    className="hf-aktion-seal-wrap md:absolute"
                                    style={{ zIndex: 2 }}
                                >
                                    <AktionSeal text={cfg.seal_text} size={220} />
                                </div>
                            </div>
                        </div>
                    </div>
                    {/* Sanfter Bottom-Fade ins Cream-Band (ca. 40 px). */}
                    <div
                        aria-hidden
                        className="absolute inset-x-0 bottom-0 h-10 pointer-events-none"
                        style={{
                            background: "linear-gradient(180deg, rgba(255,253,249,0) 0%, #FFFDF9 100%)",
                            zIndex: 2,
                        }}
                    />
                </div>

                {/* ── Band 2: Cream-Content-Panel (Subline · CTA · Slots) ── */}
                <div
                    className="relative pt-10 md:pt-14 pb-14 md:pb-20 px-4 md:px-6"
                    style={{ background: "#FFFDF9", borderBottom: "1px solid #E8DFCB" }}
                    data-testid="aktion-hero-content-band"
                >
                    {/* Auf Desktop klar links positioniert (max-w-xl, md:ml-8),
                     *  damit der rechts angeheftete Welcome-Side-Panel-Popup
                     *  daneben Platz hat. Auf Mobile bleibt alles zentriert
                     *  in voller Breite. */}
                    <div className="max-w-3xl mx-auto text-center md:mx-0 md:ml-8 md:max-w-6xl xl:max-w-[1420px] md:text-left">
                        <p
                            className="text-xl md:text-2xl font-semibold leading-snug"
                            style={{ color: "#0F1830" }}
                            data-testid="aktion-hero-subline"
                        >
                            {sublineParts.map((part, i) => (
                                /Häuser/.test(part) ? (
                                    <span key={i} style={{ color: "#DC2626", fontWeight: 800 }}>{part}</span>
                                ) : (
                                    <span key={i}>{part}</span>
                                )
                            ))}
                        </p>
                        {/* Layout:
                         *  - Desktop: SlotsChip links, dann 4 Kacheln in einer
                         *    flex-wrap-Reihe (2 CTA-Kacheln + 2 Video-Kacheln)
                         *  - Mobile:  SlotsChip oben, alle Kacheln darunter
                         *    gestacked in Column-Reihenfolge — alles permanent
                         *    sichtbar (kein Popup mehr). */}
                        <div className="mt-6 md:mt-7 flex flex-col md:flex-row md:items-start md:gap-6 gap-6">
                            <div className="flex flex-col items-center md:items-start gap-3 shrink-0">
                                {/* Der große „INFOS ERHALTEN"-Button (`aktion-hero-cta`)
                                 *  wurde aus dem Rendering entfernt — die Kacheln
                                 *  rechts (Referenz / Rabatt / Video-Teaser) sind
                                 *  jetzt die einzigen CTAs im Hero. Das CMS-Feld
                                 *  `cta_primary_text` bleibt erhalten, falls der
                                 *  Button später wieder eingebunden werden soll. */}
                                <SlotsChip remaining={remaining} total={cfg.counter_total} size="sm" />
                            </div>
                            <div
                                className="flex flex-col md:flex-row md:flex-wrap md:items-stretch gap-3 md:gap-4 flex-1 min-w-0"
                                data-testid="aktion-hero-tiles-row"
                            >
                                <HeroChoiceTiles
                                    cfg={cfg.hero_choice_tiles}
                                    onChooseReferenz={() => handleWelcomeChoice("referenz")}
                                    onChooseRabatt={() => handleWelcomeChoice("rabatt")}
                                />
                                <HeroVideoTiles cfg={cfg.hero_video_tiles} />
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ═══════════════ C) FEATURES ═══════════════ */}
            <section
                className="py-16 md:py-24 px-4 md:px-6"
                style={{ background: "#ffffff" }}
                data-testid="aktion-features"
            >
                <div className="max-w-6xl mx-auto">
                    <h2
                        className="font-anton text-4xl md:text-5xl uppercase text-center mb-2"
                        style={{ color: "#0F1830" }}
                    >
                        Das erhalten Sie — komplett kostenlos!
                    </h2>
                    <div className="mt-12 grid grid-cols-2 lg:grid-cols-4 gap-6">
                        {cfg.features.map((f, i) => (
                            <div
                                key={i}
                                className="text-center p-6 rounded-xl"
                                style={{ background: "#F8FAFC", border: "1px solid #E2E8F0" }}
                                data-testid={`aktion-feature-${i}`}
                            >
                                <div className="hf-aktion-icon-circle mx-auto">
                                    <IconFromName name={f.icon} />
                                </div>
                                <h3
                                    className="font-anton text-xl md:text-2xl uppercase mt-4"
                                    style={{ color: "#0F1830" }}
                                >
                                    {f.title}
                                </h3>
                                <p className="mt-2 text-sm md:text-base" style={{ color: "#475569" }}>
                                    {f.subtitle}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ═══════════════ C→D BRÜCKE: DAMALS vs. HEUTE ═══════════════
             *
             * Vergleichs-Sektion zwischen den Feature-Cards und der
             * „Warum kostenlos?"-Trust-Box. Erzählt eine emotionale
             * Story („früher Luxus, heute für jeden") und verankert das
             * echte 360°-Demo dort, wo es semantisch am stärksten wirkt.
             * Der Video360Viewer läuft autoplay + muted + loop wie zuvor.
             */}
            <section
                className="py-16 md:py-24 px-4 md:px-6"
                style={{ background: "#FFFDF9", borderTop: "1px solid #E8DFCB", borderBottom: "1px solid #E8DFCB" }}
                data-testid="aktion-comparison"
            >
                <div className="max-w-6xl mx-auto">
                    {/* Sektions-Header (Kicker · Serif-Titel · Subline). */}
                    <div className="text-center mb-10 md:mb-14 max-w-3xl mx-auto">
                        <div
                            className="text-[10px] md:text-xs uppercase tracking-[0.28em] mb-3"
                            style={{ color: "#8A6A2C", fontWeight: 600 }}
                            data-testid="aktion-comparison-kicker"
                        >
                            {cfg.comparison_kicker}
                        </div>
                        <h2
                            className="font-display text-3xl md:text-4xl leading-tight text-hf-ink"
                            data-testid="aktion-comparison-title"
                        >
                            {cfg.comparison_title}
                        </h2>
                        <p
                            className="mt-5 text-base md:text-lg leading-relaxed"
                            style={{ color: "hsl(var(--hf-muted))" }}
                            data-testid="aktion-comparison-subline"
                        >
                            {cfg.comparison_subline}
                        </p>
                    </div>

                    {/* Grid: linke Kachel · vs.-Divider · rechte Kachel. Mobile stacked. */}
                    <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-8 md:gap-6 items-start">
                        {/* ── Linke Kachel: universeller Slot (default = Vintage-Slideshow) ── */}
                        <figure className="flex flex-col" data-testid="aktion-comparison-vintage">
                            <div
                                className="relative w-full overflow-hidden rounded-sm"
                                style={{
                                    background: "#ffffff",
                                    padding: "14px",
                                    boxShadow: "0 12px 28px -12px rgba(30, 25, 20, 0.35)",
                                    aspectRatio: "4 / 3",
                                }}
                            >
                                <div className="w-full h-full overflow-hidden rounded-sm">
                                    <AktionComparisonSlot
                                        slot={cfg.comparison_left}
                                        testIdPrefix="aktion-cmp-left"
                                        containerBg="#FFFDF9"
                                    />
                                </div>
                            </div>
                            <figcaption
                                className="mt-4 text-sm md:text-base italic text-center font-display"
                                style={{ color: "#8A6A2C" }}
                                data-testid="aktion-comparison-vintage-label"
                            >
                                {cfg.comparison_vintage_label}
                            </figcaption>
                        </figure>

                        {/* ── Mittiger vs.-Divider (nur Desktop sichtbar) ── */}
                        <div
                            aria-hidden
                            className="hidden md:flex flex-col items-center justify-center self-stretch"
                            style={{ minWidth: "48px" }}
                        >
                            <div style={{ width: "1px", flex: 1, background: "#E8DFCB", minHeight: "40px" }} />
                            <div
                                className="font-display italic my-3"
                                style={{ color: "#D4A855", fontSize: "1.35rem" }}
                            >
                                vs.
                            </div>
                            <div style={{ width: "1px", flex: 1, background: "#E8DFCB", minHeight: "40px" }} />
                        </div>

                        {/* ── Rechte Kachel: universeller Slot (default = 360°-Video) ── */}
                        <figure className="flex flex-col" data-testid="aktion-comparison-modern">
                            <div
                                className="relative w-full overflow-hidden rounded-sm"
                                style={{
                                    background: "#0F1830",
                                    padding: "14px",
                                    boxShadow: "0 12px 28px -12px rgba(30, 25, 20, 0.4)",
                                    aspectRatio: "4 / 3",
                                }}
                            >
                                <div className="w-full h-full overflow-hidden rounded-sm" style={{ background: "#0F1830" }}>
                                    <AktionComparisonSlot
                                        slot={cfg.comparison_right}
                                        testIdPrefix="aktion-cmp-right"
                                        containerBg="#0F1830"
                                    />
                                </div>
                            </div>
                            <figcaption
                                className="mt-4 text-sm md:text-base italic text-center font-display"
                                style={{ color: "#8A6A2C" }}
                                data-testid="aktion-comparison-modern-label"
                            >
                                {cfg.comparison_modern_label}
                            </figcaption>
                            {cfg.demo_hint ? (
                                <p
                                    className="mt-1 text-xs text-center"
                                    style={{ color: "hsl(var(--hf-muted))" }}
                                >
                                    {cfg.demo_hint}
                                </p>
                            ) : null}
                        </figure>
                    </div>
                </div>
            </section>

            {/* ═══════════════ D) WARUM KOSTENLOS? ═══════════════ */}
            <section
                className="py-16 md:py-24 px-4 md:px-6"
                style={{ background: "#0F1830" }}
                data-testid="aktion-warum-kostenlos"
            >
                <div className="max-w-4xl mx-auto text-center">
                    <h2
                        className="font-anton text-4xl md:text-5xl uppercase"
                        style={{ color: "#D4A855" }}
                    >
                        {cfg.warum_kostenlos_title}
                    </h2>
                    <p
                        className="mt-6 text-lg md:text-xl leading-relaxed"
                        style={{ color: "#F1F5F9" }}
                        data-testid="aktion-warum-text"
                    >
                        {cfg.warum_kostenlos}
                    </p>
                    <div className="mt-10 flex flex-wrap justify-center gap-6">
                        {[
                            { icon: Shield, label: "UNVERBINDLICH" },
                            { icon: Lock, label: "KEINE VERPFLICHTUNG" },
                            { icon: CheckCircle2, label: "100% SICHER" },
                        ].map((b, i) => {
                            const Icon = b.icon;
                            return (
                                <div
                                    key={i}
                                    className="flex items-center gap-3 px-5 py-3 rounded-full"
                                    style={{ background: "rgba(212, 168, 85, 0.12)", border: "1px solid #D4A855" }}
                                    data-testid={`aktion-trust-badge-${i}`}
                                >
                                    <Icon size={20} style={{ color: "#D4A855" }} strokeWidth={2.2} />
                                    <span className="font-anton uppercase text-sm tracking-wider" style={{ color: "#F1F5F9" }}>
                                        {b.label}
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* ═══════════════ F) 4-SCHRITTE ═══════════════ */}
            <section
                id="ablauf"
                className="py-16 md:py-24 px-4 md:px-6"
                style={{ background: "#F8FAFC" }}
                data-testid="aktion-steps"
            >
                <div className="max-w-6xl mx-auto">
                    <h2
                        className="font-anton text-4xl md:text-5xl uppercase text-center mb-12"
                        style={{ color: "#0F1830" }}
                    >
                        So einfach geht&apos;s
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-6 md:gap-4">
                        {cfg.steps.map((s, i) => (
                            <div
                                key={i}
                                className="relative flex flex-col items-center text-center"
                                data-testid={`aktion-step-${i}`}
                            >
                                <div className="hf-aktion-step-num">{i + 1}</div>
                                <div className="hf-aktion-icon-circle mt-4" style={{ width: 52, height: 52 }}>
                                    <IconFromName name={s.icon} size={24} />
                                </div>
                                <h3
                                    className="font-anton text-lg md:text-xl uppercase mt-4"
                                    style={{ color: "#0F1830" }}
                                >
                                    {s.title}
                                </h3>
                                <p className="mt-2 text-sm" style={{ color: "#475569" }}>
                                    {s.subtitle}
                                </p>
                                {i < cfg.steps.length - 1 && (
                                    <ChevronRight
                                        aria-hidden
                                        className="hidden md:block absolute -right-4 top-4"
                                        size={32}
                                        style={{ color: "#D4A855" }}
                                    />
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ═══════════════ G) SPECIAL OFFER (Aktions-Angebot) ═══════════════
             *
             * Eigenständige Sektion — komplett unabhängig vom Hero-Zähler und
             * vom Formular-CTA. Ein/Aus-Toggle, eigener Zähler, freie
             * Typografie (Farbe/Schriftart/Größe/Fettung/Uppercase) pro Text-
             * Element. Editierbar unter /admin/aktions-seite → Block
             * „Aktions-Angebot".
             *
             * Die alte `urgency_headline` bleibt in der Config für Backward-
             * Compat, wird aber nicht mehr gerendert. */}
            {/* ═══════════════ SPECIAL-OFFER-BLOCK · ARCHIVIERT ═══════════════
             *
             * Rendering permanent entfernt (Follow-up nach Redesign).
             * Inhaltlich redundant zu den permanenten `HeroChoiceTiles`
             * direkt unter dem Video-Hero, deshalb aus dem Rendering-
             * Pfad genommen. Component-Logik, CMS-Editor und Konfig-Typ
             * `aktion_page.special_offer` bleiben erhalten, falls das
             * Element später wieder benötigt wird.
             */}

            {/* ═══════════════ H) FAQ ═══════════════
             *
             * Styling 1:1 wie Editorial-FAQ in `Home.tsx` (Accordion mit
             * ChevronDown, `font-display` Titel, dezenter Cream-BG, dünne
             * Divider). Inhalte kommen aus `aktion_page.faqs` — im Admin
             * unter /admin/aktions-seite editierbar.
             */}
            <section
                id="faq"
                data-testid="aktion-faq"
                className="py-20 md:py-28 px-4 md:px-6"
                style={{ background: "hsl(var(--hf-surface) / 0.6)" }}
            >
                <div className="max-w-3xl mx-auto">
                    {/* Sektions-Header — Editorial-Muster (Kicker + Serif-Titel) */}
                    <div className="text-center mb-10 md:mb-14">
                        <div
                            className="text-[10px] md:text-xs uppercase tracking-[0.28em] mb-3"
                            style={{ color: "#8A6A2C", fontWeight: 600 }}
                            data-testid="aktion-faq-kicker"
                        >
                            {cfg.faq_kicker}
                        </div>
                        <h2
                            className="font-display text-3xl md:text-4xl leading-tight text-hf-ink"
                            data-testid="aktion-faq-title"
                        >
                            {cfg.faq_title}
                        </h2>
                    </div>
                    <div className="divide-y" style={{ borderColor: "hsl(var(--hf-line))" }}>
                        {cfg.faqs.map((f, i) => {
                            const open = openFaq === i;
                            return (
                                <div key={i} data-testid={`aktion-faq-item-${i}`}>
                                    <button
                                        type="button"
                                        onClick={() => setOpenFaq(open ? null : i)}
                                        className="w-full flex items-center justify-between gap-4 py-5 text-left"
                                        aria-expanded={open}
                                        data-testid={`aktion-faq-toggle-${i}`}
                                    >
                                        <span className="font-display text-lg md:text-xl text-hf-ink">
                                            {f.q}
                                        </span>
                                        <ChevronDown
                                            size={20}
                                            className="shrink-0 transition-transform"
                                            style={{
                                                color: "#8A6A2C",
                                                transform: open ? "rotate(180deg)" : "rotate(0deg)",
                                            }}
                                        />
                                    </button>
                                    {open && (
                                        <div
                                            className="pb-5 text-base leading-relaxed"
                                            style={{ color: "hsl(var(--hf-muted))" }}
                                            data-testid={`aktion-faq-answer-${i}`}
                                        >
                                            {f.a}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* ═══════════════ I) KONTAKTFORMULAR ═══════════════
             *
             * Alle sichtbaren Texte + Feld-Labels + Placeholders + Privacy +
             * Erfolgs-/Fehler-Meldung sind unter /admin/aktions-seite →
             * „Kontaktformular" editierbar. Headline und Submit-Button
             * unterstützen freie Typografie (Größe/Farbe/Font/Weight/Uppercase).
             */}
            <section
                id="aktion-kontakt"
                className="py-16 md:py-24 px-4 md:px-6"
                style={{ background: cfg.contact_form.background_color || "#ffffff" }}
                data-testid="aktion-form-section"
            >
                <div className="max-w-2xl mx-auto">
                    <h2
                        className="text-center mb-3"
                        style={specialOfferStyleToCss(cfg.contact_form.headline_style)}
                        data-testid="aktion-form-headline"
                    >
                        {cfg.contact_form.headline}
                    </h2>
                    {cfg.contact_form.subline ? (
                        <p
                            className="text-center text-base md:text-lg mb-8"
                            style={{ color: "#475569" }}
                            data-testid="aktion-form-subline"
                        >
                            {cfg.contact_form.subline}
                        </p>
                    ) : null}
                    <AktionContactForm cfg={cfg.contact_form} />
                </div>
            </section>

            {/* ═══════════════ I) FOOTER ═══════════════ */}
            <SiteFooter />

            {/* ═══════════════ J) GUTSCHEIN-POPUP ═══════════════
             *
             * Modal-Overlay auf allen `/aktion`-Views. Öffnet nach Zeit
             * (`trigger_seconds`) oder Scroll-Prozent
             * (`trigger_scroll_percent`) — was zuerst zutrifft.
             * SessionStorage-Dismissal (`hf_gutschein_dismissed`) verhindert
             * erneutes Öffnen in derselben Tab-Session.
             */}
            <GutscheinPopup
                cfg={cfg.gutschein_popup}
                fallbackScrollTargetId="aktion-kontakt"
                mode={manualMode}
                referenzCfg={cfg.referenz_popup}
                openSignal={openSignal}
                // Auto-Trigger (Zeit + Scroll) ist auf `/aktion` permanent
                // deaktiviert: der Rabatt-Popup soll ausschließlich über
                // einen expliziten Karten-Klick („80 % RABATT SICHERN")
                // aufgehen — kein Nachtrigger, wenn der Nutzer das Welcome
                // dismissed hat. Manuelle Öffnungen laufen über `openSignal`
                // und ignorieren dieses Flag.
                suspendAutoTrigger={true}
            />

            {/* ═══════════════ WELCOME-CHOICE-POPUP · ARCHIVIERT ═══════════════
             *
             * Rendering permanent entfernt. Die Auswahl läuft jetzt
             * ausschließlich über die permanenten `HeroChoiceTiles` neben
             * dem CTA-Block. Component-Code, CMS-Editor und der Konfig-
             * Typ bleiben erhalten für den Fall, dass die Auto-Öffnung
             * später wieder aktiviert wird.
             *
             * WICHTIG: Nicht wieder einkommentieren, ohne vorher die
             * Kacheln zu deaktivieren — sonst duplizierte Auswahl-UX.
             */}
        </div>
    );
}
